<template>
  <div :data-testid="testId" class="tw-h-full tw-flex">
    <div class="tw-flex tw-flex-1 tw-flex-col">
      <div
        class="tw-py-4 tw-h-full tw-overflow-y-auto"
        data-testid="meeting-preparation-remarks"
      >
        <div
          v-if="loading"
          class="tw-flex tw-justify-center tw-items-center spinner-container"
        >
          <div class="sf-spinner tw-w-8 tw-h-8"></div>
        </div>
        <MainEmptyState v-else-if="empty" />
        <div v-else class="tw-flex tw-flex-col">
          <div
            class="
              tw-my-4
              tw-mx-8
              tw-p-4
              tw-border
              tw-border-sf-neutral-lighter-1
              tw-rounded-lg
              tw-bg-sf-neutral-subtle-1
            "
          >
            <LinksSection
              :links="pageData.links"
              :reports="pageData.reports"
              :available-reports="pageData.availableReports"
              editable
              display-title
            />
          </div>

          <div
            v-if="unorderedRemarks.length"
            class="tw-relative"
            data-testid="meeting-unordered-remarks"
          >
            <div class="tw-my-5 tw-mx-8 tw-h-px tw-bg-sf-danger-light-1" />
            <div
              class="
                tw-absolute
                tw-top-0
                tw-left-1/2
                tw--translate-x-1/2
                tw-flex
                tw-flex-row
                tw-items-center
                tw-gap-1
                tw-h-8
                tw-my-1
                tw-rounded
                tw-px-2
                tw-bg-sf-neutral-subtle-2
                tw-border
                tw-border-solid
                tw-border-sf-danger-light-1/20
              "
            >
              <span class="tw-text-sf-neutral-darker-1 tw-font-medium">
                {{ I18n.t('client_meetings.prepare.new_messages') }}
              </span>
              <SfButtonDs
                size="sm"
                variant="secondary"
                data-testid="meeting-dismiss-new-button"
                @click="joinUnordered"
              >
                <span class="tw-text-sf-neutral">OK</span>
              </SfButtonDs>
            </div>
            <Draggable
              v-model="unorderedRemarks"
              item-key="id"
              group="remarks"
              handle=".handle"
              class="tw-flex tw-flex-col tw-gap-4"
              :animation="200"
              :scroll="false"
              :force-fallback="true"
              @start="onDragStart"
              @end="onDragEnd"
            >
              <template #item="{ element: remark }">
                <TalkingPoint
                  :remark="remark"
                  :dragging="dragging"
                  handle-class="handle"
                />
              </template>
            </Draggable>
            <div class="tw-my-5 tw-mx-8 tw-h-px tw-bg-sf-danger-light-1" />
          </div>

          <Draggable
            v-model="orderedItems"
            data-testid="meeting-ordered-remarks"
            item-key="id"
            group="remarks"
            handle=".handle"
            class="tw-flex tw-flex-col"
            :class="orderedRemarksClass"
            :animation="200"
            :scroll="false"
            :force-fallback="true"
            @start="onDragStart"
            @end="onDragEnd"
          >
            <template #item="{ element: item }">
              <div class="tw-group">
                <ActionBar
                  v-if="addingTitle == ''"
                  @add-title="addingTitle = item.id"
                  @add-message="() => addTalkingPoint(item.id)"
                />
                <div v-else class="tw-my-4" />

                <div v-if="addingTitle === item.id" class="tw-mx-8 tw-my-4">
                  <TitleForm
                    :title="{ content: '', size: '' }"
                    @update-title="
                      (payload) => createTitle(payload, toTypedId(item))
                    "
                    @dismissed="addingTitle = ''"
                  />
                </div>
                <div v-else-if="editingTitle === item.id" class="tw-mx-8">
                  <TitleForm
                    :title="item"
                    @update-title="updateTitle"
                    @dismissed="editingTitle = ''"
                    @remove-title="removeTitle(item.id)"
                  />
                </div>

                <TalkingPoint
                  v-if="item.kind == 'remark-card'"
                  :remark="item"
                  :dragging="dragging"
                  handle-class="handle"
                />
                <TitleCard
                  v-else-if="editingTitle !== item.id && item.kind == 'title'"
                  :title="item"
                  :dragging="dragging"
                  @click="editingTitle = item.id"
                  @remove-title="removeTitle(item.id)"
                />
              </div>
            </template>
          </Draggable>

          <ActionBar
            v-if="addingTitle == ''"
            @add-title="addingTitle = LAST_IN_ORDER"
            @add-message="() => addTalkingPoint(LAST_IN_ORDER)"
          />

          <div v-if="addingTitle === LAST_IN_ORDER" class="tw-mx-8 tw-my-4">
            <TitleForm
              :title="{ content: '', size: '' }"
              @update-title="(payload) => createTitle(payload, LAST_IN_ORDER)"
              @dismissed="addingTitle = ''"
            />
          </div>
          <div v-else-if="editingTitle === LAST_IN_ORDER" class="tw-mx-8">
            <TitleForm
              :title="item"
              @update-title="updateTitle"
              @dismissed="editingTitle = ''"
              @remove-title="removeTitle(LAST_IN_ORDER)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <ExternalNoteModal />
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, inject } from 'vue'
import { useStore } from 'vuex'

import Draggable from 'vuedraggable'

import { isUsingNewLayout } from '@app/adapters/silverfin'
import TalkingPoint from '@app/application/client_meetings/screens/prepare/components/talking-point.vue'
import TitleCard from '@app/application/client_meetings/screens/prepare/components/title-card.vue'
import ActionBar from '@app/application/client_meetings/screens/prepare/components/action-bar.vue'
import TitleForm from '@app/application/client_meetings/screens/prepare/components/title-form.vue'

import LinksSection from '@app/application/client_meetings/screens/prepare/components/links-section.vue'
import MainEmptyState from './main-empty-state.vue'
import ExternalNoteModal from '@app/application/client_meetings/screens/prepare/components/external-note-modal/external-note-modal.vue'
import { SfButtonDs } from '@silverfin/ui-components'

import { useSuggestedTalkingPointsStore } from '@app/application/client_meetings/screens/prepare/stores/suggested-talking-points'
import useOrderedRemarks, {
  LAST_IN_ORDER,
} from '@app/application/client_meetings/screens/prepare/composables/use-ordered-remarks'
import { useTalkingPointTag } from '@app/application/client_meetings/composables/use-talking-point-tag'

import useOpenRemarkModal from '@app/shared/vue/composables/use-open-remark-modal'
import useDragAutoscroll from '@app/shared/vue/composables/use-drag-autoscroll'

import scrollHelper from '@app/application/communication/sections/communication/components/mixins/scroll-helper.js'
import { toTypedId } from '@app/application/client_meetings/screens/prepare/utils'

const pageData = inject('pageData')
const store = useStore()

const suggestedTalkingPointsStore = useSuggestedTalkingPointsStore()

defineOptions({
  mixins: [scrollHelper],
  methods: {
    handleAnchorScroll(callback) {
      const anchorSelector = this.elementAnchorLink?.()

      if (!anchorSelector) return

      const container = document.querySelector(
        '[data-testid="meeting-preparation-remarks"]'
      )
      const target = container?.querySelector(anchorSelector)

      if (!container || !target) return

      const scrollOffset = target.offsetTop - container.offsetTop

      const usingNewLayout = isUsingNewLayout()

      if (usingNewLayout) {
        target?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      } else {
        container.scrollTo({
          top: scrollOffset,
          behavior: 'smooth',
        })
      }

      this.anchorElementScrolled = true

      if (callback) {
        callback()
      }
    },
  },
})

store.dispatch('setInitialOrder', pageData.initialOrder)

const {
  orderedItems,
  unorderedRemarks,
  allRemarks,
  settingOrder,
  joinUnordered,
} = useOrderedRemarks()

const orderedRemarksClass = computed(() => {
  if (unorderedRemarks.value.length > 0) {
    return 'tw-opacity-50'
  }
  return ''
})

const dragging = ref(false)
const addingTitle = ref('')
const editingTitle = ref('')
const addingMessageBeforeId = ref('')

const { onDragStart: startAutoscroll, onDragEnd: stopAutoscroll } =
  useDragAutoscroll({
    topBoundary: () => {
      const subheader = document.getElementById('secondary-subheader')
      return subheader ? subheader.getBoundingClientRect().bottom : 0
    },
  })

function onDragStart(event) {
  dragging.value = true
  startAutoscroll(event)
}

function onDragEnd() {
  dragging.value = false
  stopAutoscroll()
}

const empty = computed(() => allRemarks.value.length === 0)
const loading = computed(
  () => empty.value && store.getters.fetchingCommunicationsInProgress
)
const testId = computed(() =>
  settingOrder.value ? null : 'meeting-preparation-idle'
)

async function refreshCommunicationData(data = {}) {
  await store.dispatch('fetchCommunication')

  if (data.action === 'createRemark') {
    window.eventHub.$emit('highlightRemark', {
      elementId: `remark-${data.remark.id}`,
      force: true,
    })

    if (data.callbackPayload && data.callbackPayload.suggestionText) {
      suggestedTalkingPointsStore.removeSuggestion(
        data.callbackPayload.suggestionText,
        data.remark.id
      )
      if (suggestedTalkingPointsStore.suggestionCount === 0) {
        suggestedTalkingPointsStore.fetchSuggestions()
      }
    }
  }

  if (Object.keys(data).length !== 0) {
    if (addingMessageBeforeId.value === LAST_IN_ORDER) {
      orderedItems.value = orderedItems.value.toSpliced(
        orderedItems.value.length,
        0,
        data.remark
      )
      addingMessageBeforeId.value = ''
    } else if (addingMessageBeforeId.value !== '') {
      const index = orderedItems.value.findIndex(({ id }) => {
        return id.toString() === addingMessageBeforeId.value.toString()
      })
      orderedItems.value = orderedItems.value.toSpliced(index, 0, data.remark)

      addingMessageBeforeId.value = ''
    } else {
      joinUnordered()
    }
  }

  const counterSpan = document.querySelectorAll(
    '[data-behaviour="talking-points-counter"]'
  )
  const counterSlot = document.querySelectorAll(
    '[data-behaviour="talking-points-counter-slot"]'
  )
  if (allRemarks.value.length === 0) {
    counterSpan.forEach((span) => {
      span.style.display = 'none'
    })
    counterSlot.forEach((slot) => {
      slot.style.display = 'none'
    })
  } else {
    counterSpan.forEach((span) => {
      span.textContent = allRemarks.value.length
      span.style.display = ''
    })
    counterSlot.forEach((slot) => {
      slot.style.display = ''
    })
  }
}
store.dispatch('fetchMentionUsernames')
refreshCommunicationData()

function createTitle(payload, insertBeforeId) {
  if (payload.content) {
    void store.dispatch('createTitle', { ...payload, insertBeforeId })
    addingTitle.value = ''
  }
}

function updateTitle(payload) {
  if (payload.content) {
    void store.dispatch('updateTitle', payload)
  }
}

function removeTitle(id) {
  void store.dispatch('removeTitle', id)
}

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})

function addTalkingPoint(beforeId) {
  addingMessageBeforeId.value = beforeId
  openNewRemarkModal()
}

const { talkingPointTagIds: selectedTagIds } = useTalkingPointTag()

const openNewRemarkModal = useOpenRemarkModal({
  ownerType: 'ClientMeeting',
  ownerId: pageData.clientMeetingId,
  remarkText: true,
  hidePeriodSelector: true,
  selectedTagIds: selectedTagIds,
  modalSize: 'l',
  isClientMeetingRemark: true,
})
</script>

<style lang="scss" scoped>
.spinner-container {
  height: calc(100vh - 16rem);
}
</style>
